import { useRef, useState } from "react";
import { Button, CircularProgress, Typography } from "@mui/material";
import { useReactFlow } from "@xyflow/react";
import __ from "src/utils/Translations";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { useStructureStore } from "src/Pages/Company/OrganizationStructure/context/useStructureStore";
import useOrgchartEmployeesStore from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Employees/store";
import {
  fetchEmployeeOrgchartWithExclusions,
  processChildrenNodes,
  IEmployeeInOrgchartChild,
} from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Employees/api";
// eslint-disable-next-line
import { getAutoLayoutWorker } from "../../workers/getOrgchartWebWorker";

interface SearchLoadMoreHandleProps {
  supervisorId: string;
}

const SearchLoadMoreHandle = ({ supervisorId }: SearchLoadMoreHandleProps) => {
  const [loading, setLoading] = useState(false);
  const { setCenter, getNode } = useReactFlow();
  const companyId = useCompanyId();
  const structureId = useStructureStore((state) => state.structureId) || "";
  const workerRef = useRef<Worker | null>(null);

  // eslint-disable-next-line no-eq-null, eqeqeq
  if (workerRef.current == null) {
    workerRef.current = getAutoLayoutWorker();
  }

  const handleClick = async () => {
    setLoading(true);
    const store = useOrgchartEmployeesStore.getState();

    // Get cursor for this supervisor
    const cursor = store.paginationCursors.get(supervisorId);
    // Extract all employee IDs from hierarchy data to exclude
    const extractEmployeeIds = (
      children: IEmployeeInOrgchartChild[]
    ): string[] => {
      const ids: string[] = [];
      children.forEach((child) => {
        ids.push(child.employeeId);
        if (child.children?.length) {
          ids.push(...extractEmployeeIds(child.children));
        }
      });
      return ids;
    };

    const excludeIds = store.hierarchyData
      ? extractEmployeeIds(store.hierarchyData.children)
      : [];

    const response = await fetchEmployeeOrgchartWithExclusions(
      companyId,
      structureId,
      supervisorId,
      excludeIds,
      cursor
    );
    const { nodes: newNodes, edges: newEdges } = processChildrenNodes(
      response,
      supervisorId
    );

    const existingNodeIds = new Set(store.nodes.map((n) => n.id));
    const existingEdgeIds = new Set(store.edges.map((e) => e.id));

    const filteredNodes = newNodes.filter((n) => !existingNodeIds.has(n.id));
    const filteredEdges = newEdges.filter((e) => !existingEdgeIds.has(e.id));

    const mergedNodes = [...store.nodes, ...filteredNodes];
    const mergedEdges = [...store.edges, ...filteredEdges];

    store.setAllNodes(mergedNodes);
    store.setSearchedNodeId(null);

    // Store next cursor if present
    if (response.nextCursor) {
      store.setPaginationCursor(supervisorId, response.nextCursor);
    } else {
      store.paginationCursors.delete(supervisorId);
    }

    if (workerRef.current) {
      workerRef.current.postMessage({ nodes: mergedNodes, edges: mergedEdges });
      workerRef.current.onmessage = (event) => {
        store.setNodes(event.data.nodes);
        store.setEdges(event.data.edges);

        setTimeout(() => {
          if (newNodes.length > 0) {
            const middleIndex = Math.floor(newNodes.length / 2);
            const targetNode = getNode(newNodes[middleIndex].id);

            if (targetNode?.position) {
              setCenter(targetNode.position.x + 170, targetNode.position.y, {
                zoom: 0.6,
                duration: 800,
              });
            }
          }
          setLoading(false);
        }, 400);
      };
    }
  };

  return (
    <Button
      onClick={handleClick}
      variant="outlined"
      sx={{
        minWidth: "150px",
        position: "absolute",
        bottom: "50%",
        right: "-50%",
        transform: "translateY(50%)",
        padding: "0.375rem 1rem",
        backgroundColor: "white",
        border: "1px solid #BDBDBD",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10,
      }}
    >
      {loading ? (
        <CircularProgress size={16} color="primary" />
      ) : (
        <Typography
          color="textPrimary"
          variant="body2"
          sx={{ fontWeight: "700" }}
        >
          {__("Pokaż więcej")} +
        </Typography>
      )}
    </Button>
  );
};

export default SearchLoadMoreHandle;
